import { Color, Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon as CaretDownIconSmall } from '@persona-id/design-tokens/icons';
import { isEmpty } from 'lodash';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import TruncatedText from 'dashboard/components/TruncatedText';
import { REPORT_FILTER_META, ReportFilterCondition } from 'dashboard/lib/reports/filterMetas';
import { ReportTemplate } from 'dashboard/models/report-template';
import ReportAdvancedFilterRow from 'dashboard/views/DashboardReportsView/ReportAdvancedFilterRow';
import Button from 'design-system/components/Button';
import Popover from 'design-system/components/Popover/Popover';
import Spacer from 'design-system/components/Spacer';
import inputMixin, { InputMixinProps, InputSizes } from 'design-system/mixins/input';
import useIsMounted from 'lib/hooks/useIsMounted';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

const Container = styled('div')`
  position: relative;
`;

const Placeholder = styled('div')<InputMixinProps>`
  ${inputMixin};

  min-width: 200px;

  /* Fixes weird issue with shadow appearing inside placeholder text */
  user-select: none;

  cursor: pointer;
  &:disabled {
    cursor: default;
  }
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledIconCaretDownSmall = styled(CaretDownIconSmall)`
  pointer-events: none;
`;

const StyledButton = styled(Button)`
  background-color: ${Color.CoreSlate50};
  height: ${Spacing[6]};
`;

const Well = styled('div')`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[3]};
`;

const StyledAndButton = styled(Button)`
  background-color: ${Color.CoreSlate200};
  width: ${Spacing[8]};
  height: ${Spacing[6]};
`;

const DEFAULT_EMPTY_ROW: ReportFilterCondition = {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  template: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  property: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  operator: undefined,
  values: [],
};

const getValidFilters = (filters: ReportFilterCondition[][], templateTypeMap: TemplateIdType) => {
  return filters.reduce((groups, group) => {
    const validRows = group.filter(
      (row) =>
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        !isEmpty(row.template) && REPORT_FILTER_META[templateTypeMap[row.template]].isValid(row),
    );
    if (validRows.length > 0) {
      groups.push(validRows);
    }
    return groups;
  }, [] as ReportFilterCondition[][]);
};

const SUPPORTED_TEMPLATE_TYPES = [
  ApiModel.ReportTemplateAddressLookup,
  ApiModel.ReportTemplateAdverseMedia,
  ApiModel.ReportTemplateBusinessAdverseMedia,
  ApiModel.ReportTemplateBusinessAssociatedPersons,
  ApiModel.ReportTemplateBusinessLookup,
  ApiModel.ReportTemplateBusinessWatchlist,
  ApiModel.ReportTemplateEmailAddress,
  ApiModel.ReportTemplatePhoneNumber,
  ApiModel.ReportTemplatePoliticallyExposedPerson,
  ApiModel.ReportTemplateSynthetic,
  ApiModel.ReportTemplateWatchlist,
];

interface TemplateIdType {
  [key: string]: ReportTemplate['type'];
}

export interface ReportAdvancedFilterProps {
  values?: ReportFilterCondition[][];
  templateOptions: { value: string; type: ReportTemplate['type']; label: string }[];
  onChange: (filters: ReportFilterCondition[][]) => void;
  size?: InputSizes;
  className?: string;
}

const ReportAdvancedFilter = ({
  values,
  templateOptions,
  onChange,
  size = 'md',
  className,
}: ReportAdvancedFilterProps) => {
  const [showFilterDropdown, { disable: hideFilterDropdown, set: setFilterDropdown }] =
    useToggle(false);
  const [filterGroups, setFilterGroups] = useState<ReportFilterCondition[][]>(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    !isEmpty(values) ? values : [[DEFAULT_EMPTY_ROW]],
  );

  const templates = templateOptions.filter((template) =>
    SUPPORTED_TEMPLATE_TYPES.includes(template.type),
  );

  const templateIdTypeMap: TemplateIdType = templateOptions.reduce(
    (map: TemplateIdType, option) => {
      map[option.value] = option.type;
      return map;
    },
    {} as TemplateIdType,
  );

  const handleAddRow = (groupIndex: number) => () => {
    const groups = [...filterGroups];
    groups[groupIndex] = [...filterGroups[groupIndex]];
    groups[groupIndex].push(
      groups[groupIndex].length >= 1 ? { ...groups[groupIndex][0], values: [] } : DEFAULT_EMPTY_ROW,
    );
    setFilterGroups(groups);
  };

  const handleRemoveRow = (groupIndex: number, rowIndex: number) => () => {
    const groups = [...filterGroups];
    if (groups[groupIndex].length === 1) {
      groups.splice(groupIndex, 1);
      if (groups.length === 0) {
        groups.push([DEFAULT_EMPTY_ROW]);
        hideFilterDropdown();
      }
    } else {
      groups[groupIndex] = [...filterGroups[groupIndex]];
      groups[groupIndex].splice(rowIndex, 1);
    }
    setFilterGroups(groups);
  };

  const handleTemplateUpdated = (groupIndex: number) => (template: string) => {
    const groups = [...filterGroups];
    groups[groupIndex] = [
      {
        ...DEFAULT_EMPTY_ROW,
        template,
      },
    ];
    setFilterGroups(groups);
  };

  const updateRowValues =
    (groupIndex: number, rowIndex: number) => (row: Partial<ReportFilterCondition>) => {
      const groups = [...filterGroups];
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      groups[groupIndex][rowIndex] = {
        ...groups[groupIndex][rowIndex],
        ...row,
      };
      setFilterGroups(groups);
    };

  const validFilters = getValidFilters(filterGroups, templateIdTypeMap);

  const getSummaryLabelText = useCallback(() => {
    const numValidFilters = validFilters.reduce((acc, group) => (acc += group.length), 0);
    if (numValidFilters === 0) {
      return 'Add Filter';
    } else if (numValidFilters === 1) {
      return '1 Filter';
    } else {
      return `${numValidFilters} Filters`;
    }
  }, [validFilters]);

  const isMounted = useIsMounted();
  useEffect(() => {
    if (!isMounted()) {
      return;
    }
    onChange(validFilters);
  }, [JSON.stringify(validFilters)]);

  const dropdown = (
    <>
      {filterGroups.map((rows: ReportFilterCondition[], groupIndex: number) => (
        <Fragment key={`${groupIndex}-${rows[0].template}`}>
          <Well data-test={`report-advanced-filter__group-${groupIndex}`}>
            {rows.map((row: ReportFilterCondition, rowIndex: number) => (
              <Fragment key={`${groupIndex}-${rowIndex}-${row.template}`}>
                <ReportAdvancedFilterRow
                  id={`${groupIndex}-${rowIndex}`}
                  label={rowIndex === 0 ? (groupIndex > 0 ? 'Or' : 'Where') : 'And'}
                  row={row}
                  disableTemplate={rowIndex > 0}
                  templateOptions={templates}
                  templateType={templateIdTypeMap[row.template]}
                  handleTemplateUpdated={handleTemplateUpdated(groupIndex)}
                  handleRowUpdated={updateRowValues(groupIndex, rowIndex)}
                  remove={handleRemoveRow(groupIndex, rowIndex)}
                />
                <Spacer height={1} />
              </Fragment>
            ))}
            <StyledAndButton
              data-test={`report-advanced-filter__and-button-${groupIndex}`}
              id={`report-advanced-filter__and-button-${groupIndex}`}
              variant='basic'
              size='sm'
              width='auto'
              onClick={handleAddRow(groupIndex)}
            >
              And
            </StyledAndButton>
          </Well>
          <Spacer height={2} />
        </Fragment>
      ))}
      <StyledButton
        data-test='report-advanced-filter__or-button'
        id='report-advanced-filter__or-button'
        variant='basic'
        size='sm'
        width='auto'
        onClick={() => setFilterGroups([...filterGroups, [DEFAULT_EMPTY_ROW]])}
      >
        Or group
      </StyledButton>
    </>
  );

  return (
    <Container className={className}>
      <Popover
        trigger={
          <Placeholder
            role='button'
            data-test='report-advanced-filter__placeholder'
            id='report-advanced-filter__placeholder'
            active={showFilterDropdown}
            $size={size}
          >
            <TruncatedText>{getSummaryLabelText()}</TruncatedText>
            <StyledIconCaretDownSmall />
          </Placeholder>
        }
        open={showFilterDropdown}
        onOpenChange={(open) => setFilterDropdown(open)}
        contentProps={{
          side: 'bottom',
          align: 'start',
          collisionBoundary: [document.getElementById('main')],
        }}
      >
        {dropdown}
      </Popover>
    </Container>
  );
};

export default ReportAdvancedFilter;
